import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { useApi, type Resource } from './api'
import { deriveKeyId, normalizeToken } from './keyId'
import { useSession } from './session'
import { readStored, writeStored } from './stored'
import type { EnrolledKey } from './types'

/** None entered, not a token, waiting for the enrollments, not one the user is enrolled for, or set. */
export type TokenState = 'none' | 'invalid' | 'checking' | 'unknown' | 'set'

export interface TokenCheck {
  state: TokenState
  /** The token's key ID; null while it is not a token. */
  keyId: string | null
  /** The user's enrollment for that key ID. */
  match: EnrolledKey | undefined
}

/** Checks a token against the user's enrollments, entirely in the browser (TOK-07). */
export function checkToken(token: string, keys: readonly EnrolledKey[] | undefined): TokenCheck {
  if (!token.trim()) return { state: 'none', keyId: null, match: undefined }
  const normalized = normalizeToken(token)
  if (!normalized) return { state: 'invalid', keyId: null, match: undefined }
  const keyId = deriveKeyId(normalized)
  if (!keys) return { state: 'checking', keyId, match: undefined }
  const match = keys.find((key) => key.key_id === keyId)
  return { state: match ? 'set' : 'unknown', keyId, match }
}

/** How long a browser remembers the token before the user enters it again (TOK-06, D65). */
export const REMEMBER_DAYS = 90
const REMEMBER_MS = REMEMBER_DAYS * 24 * 60 * 60 * 1000

/** A token this browser remembers for an account, with when it was first remembered (ms since the epoch). */
export interface Remembered {
  token: string
  since: number
}

/**
 * The stored token, unless it has expired or is not one this rule wrote, such as a bare token stored
 * before remembering expired: either is dropped, and the user enters the token again.
 */
export function readRemembered(raw: string | null, now: number): Remembered | null {
  if (raw === null) return null
  try {
    const value = JSON.parse(raw) as Partial<Remembered> | null
    const { token, since } = value ?? {}
    if (typeof token !== 'string' || !normalizeToken(token) || typeof since !== 'number') return null
    return since <= now && now - since < REMEMBER_MS ? { token, since } : null
  } catch {
    return null
  }
}

/**
 * What the browser keeps now: the token while it is set and the user wants it remembered, from the day it
 * was first remembered; nothing once it is not set, as after its key ID was retired or the user's
 * enrollment ended; and no change while the enrollments are loading or could not be fetched.
 */
export function toRemember(
  state: TokenState,
  remember: boolean,
  token: string,
  stored: Remembered | null,
  now: number,
): Remembered | null | undefined {
  if (state === 'checking') return undefined
  const normalized = normalizeToken(token)
  if (!remember || state !== 'set' || !normalized) return null
  return { token: normalized, since: stored?.token === normalized ? stored.since : now }
}

export interface SiteToken extends TokenCheck {
  /** The token as entered; it never leaves this browser (TOK-02). */
  token: string
  setToken: (token: string) => void
  /** Kept in this browser for this account only when the user asks, for 90 days at most (TOK-06). */
  remember: boolean
  setRemember: (remember: boolean) => void
  /** Clears the token here and in the browser's storage. */
  forget: () => void
  /** Everything the user is enrolled for. */
  keys: Resource<EnrolledKey[]>
  /** While an upload checks or sends files with the token, it cannot be changed. */
  locked: boolean
  setLocked: (locked: boolean) => void
}

const TokenContext = createContext<SiteToken | null>(null)

/**
 * The site token for every signed-in page, entered once at the navigation's foot: uploads need it
 * set (`components/TokenStatus.tsx`). It stays in this page's memory until forgotten or the website is
 * left, and in the browser's storage only when remembered, until it expires or is no longer set.
 */
export function TokenProvider({ children }: { children: ReactNode }) {
  const { me } = useSession()
  const keys = useApi<EnrolledKey[]>('/me/keys')
  // The enrollments and the sites' configurations change on other pages and in other tabs (TOK-05,
  // TOK-28): the tab asks again whenever it comes back, so uploads never check files with an outdated one.
  const { poll } = keys
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') poll()
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [poll])
  const storageKey = `pedfx.token.${me.id}`
  const [stored] = useState(() => readRemembered(readStored(storageKey), Date.now()))
  const [token, setToken] = useState(stored?.token ?? '')
  const [remember, setRemember] = useState(stored !== null)
  const [locked, setLocked] = useState(false)
  const check = useMemo(() => checkToken(token, keys.data), [token, keys.data])
  useEffect(() => {
    const now = Date.now()
    const next = toRemember(check.state, remember, token, readRemembered(readStored(storageKey), now), now)
    if (next !== undefined) writeStored(storageKey, next && JSON.stringify(next))
  }, [storageKey, remember, check.state, token])
  const forget = useCallback(() => {
    setToken('')
    setRemember(false)
  }, [])
  const value = useMemo(
    () => ({ ...check, token, setToken, remember, setRemember, forget, keys, locked, setLocked }),
    [check, token, remember, forget, keys, locked],
  )
  return <TokenContext value={value}>{children}</TokenContext>
}

export function useSiteToken(): SiteToken {
  const token = useContext(TokenContext)
  if (token === null) throw new Error('useSiteToken must be used inside TokenProvider')
  return token
}
